import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { apiRequestV2 } from "@/lib/queryClient";
import { faucetKeys } from "@/constant/queryKeys";
import { useAuthStore } from "@/hooks/useAuthStore";

// Types
interface ClaimTokensRequest {
    partnerId: number;
    walletAddress: string;
    amount: number;
}

interface ClaimTokensResponse {
  success: boolean;
  signature: string;
  amount: number;
  message: string;
}

interface FaucetStatusResponse {
  success: boolean;
  claimsToday: number;
  remainingClaims: number;
  lastClaimTime?: string;
  nextClaimTime?: string;
}

interface FaucetStatsResponse {
  success: boolean;
  faucetBalance: number;
  totalDistributed: number;
  totalClaims: number;
}

interface FaucetConfigResponse {
  success: boolean;
  maxClaims: number;
  minAmount: number;
  maxAmount: number;
  tokenName: string | null;
  isOnlyEligibleForGeneratedWallet: boolean;
  isDailyResetClaim: boolean;
}

export function postClaimFaucetOptions() {
    return mutationOptions({
        mutationKey: faucetKeys.claim,
        mutationFn: (data: ClaimTokensRequest) => apiRequestV2<ClaimTokensResponse>('POST', "/api/v1/faucet/claim", data),
    })
}

export function getFaucetStatusOptions(partnerId?: number, walletAddress?: string) {
    return queryOptions({
        queryKey: faucetKeys.status(partnerId, walletAddress),
        queryFn: () => apiRequestV2<FaucetStatusResponse>('GET', `/api/v1/faucet/${partnerId}/status/${walletAddress}`),
        enabled: !!walletAddress && !!partnerId,
        refetchInterval: 10000, // Refetch every 10 seconds to update countdown
    })
}

// Get faucet statistics
export function getFaucetStatsOptions(partnerId?: number) {
  return queryOptions({
    queryKey: faucetKeys.stats(partnerId),
    queryFn: () => apiRequestV2<FaucetStatsResponse>("GET", `/api/v1/faucet/${partnerId}/stats`),
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: !!partnerId,
  });
}

// Get faucet configuration based on egg's lock token
export function getFaucetConfigOptions(partnerId?: number) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return queryOptions({
    queryKey: faucetKeys.config(partnerId),
    queryFn: () => apiRequestV2<FaucetConfigResponse>("GET", `/api/v1/faucet/${partnerId}/config`),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(partnerId && isAuthenticated),
  });
}

// Airdrop lock tokens for devnet testing (connected wallets verify ownership, then receive tokens)
interface AirdropLockTokenRequest {
  walletAddress: string;
  tokenMint: string;
  partnerId: number;
  message: string;
  signature: string;
  amount?: string; // Optional: specific amount to airdrop (in smallest units). If not provided, uses maxAmountPerClaim from config
}

interface AirdropLockTokenResponse {
  success: boolean;
  signature: string;
  message: string;
}

export function airdropLockTokenOptions() {
  return mutationOptions({
    mutationKey: faucetKeys.airdropLockToken,
    mutationFn: (request: AirdropLockTokenRequest) =>
      apiRequestV2<AirdropLockTokenResponse>(
        "POST",
        "/api/v1/faucet/airdrop-lock-token",
        request
      ),
  });
}
